import { jsx, jsxs } from 'react/jsx-runtime';
import { lazy, useState, useMemo, useEffect, useCallback, Suspense } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useCheckoutContext } from '@zooplus/checkout-state';
import { ADDRESS_PROJECT_ID } from '../constant/constants.js';
import { AddressActionsContext } from '../context/address-context.js';
import ReadonlyAddress from '../readonly-address/readonly-address.js';
import { AddressComponentState } from '../types/index.js';
import { Fallback } from '../../fallback/fallback.js';
import { Loader } from '../../loader/loader.js';
import { FlagsProvider } from '../../../context/flags-context.js';
import { handleError } from '../../../services/error-boundary-service.js';

const DeliveryAddressForm = lazy(() => import('../delivery-address/delivery-address-form.js'));
const InvoiceAddressForm = lazy(() => import('../invoice-address/invoice-address-form.js'));
const SavedAddresses = lazy(() => import('../delivery-address/checkout/saved-addresses.js'));
const ADDRESS_SECTION_ID = 'address-component-switcher';
const AddressComponentSwitcher = () => {
    const { customerAddressesState = {} } = useCheckoutContext(ADDRESS_PROJECT_ID);
    const { addressMessages, deliveryAddress, invoiceAddress, isDeliveryAndInvoiceCollapsed, setIsDeliveryAndInvoiceCollapsed = () => { }, } = customerAddressesState;
    const [showBackToListingLink, setShowBackToListingLink] = useState(false);
    const { initialState, initialAddress, isCombinedForm } = useMemo(() => {
        let initialState = AddressComponentState.ADD_NEW_DELIVERY_ADDRESS;
        let initialAddress = {};
        if (!deliveryAddress && !invoiceAddress) {
            return { initialState, isCombinedForm: true, initialAddress };
        }
        const isCombinedForm = false;
        if (!deliveryAddress) ;
        else if (!invoiceAddress) {
            initialState = AddressComponentState.ADD_NEW_INVOICE_ADDRESS;
        }
        else {
            if (!deliveryAddress.addressValidBasedOnRules) {
                initialState = AddressComponentState.EDIT_DELIVERY_ADDRESS;
                initialAddress = deliveryAddress;
            }
            else if (!deliveryAddress.addressValidatedWithUniserv) {
                initialState = AddressComponentState.EDIT_DELIVERY_ADDRESS;
                initialAddress = deliveryAddress;
                initialAddress.onLoadUniservValidation = true;
            }
            else if (!invoiceAddress.addressValidBasedOnRules) {
                initialState = AddressComponentState.EDIT_INVOICE_ADDRESS;
                initialAddress = invoiceAddress;
            }
            else {
                initialState = AddressComponentState.READONLY_ADDRESS;
            }
        }
        return { initialState, isCombinedForm, initialAddress };
    }, [deliveryAddress, invoiceAddress]);
    useEffect(() => {
        if (initialState !== AddressComponentState.READONLY_ADDRESS) {
            setIsDeliveryAndInvoiceCollapsed(false);
        }
    }, [initialState, setIsDeliveryAndInvoiceCollapsed]);
    const [addressComponentState, setAddressComponentState] = useState(initialState);
    const [currentAddress, setCurrentAddress] = useState(initialAddress);
    const addNewDeliveryAddress = useCallback(() => {
        setAddressComponentState(AddressComponentState.ADD_NEW_DELIVERY_ADDRESS);
    }, []);
    const editDeliveryAddress = useCallback((address, showBackToListingLink = true) => {
        setCurrentAddress(address);
        setShowBackToListingLink(showBackToListingLink);
        setAddressComponentState(AddressComponentState.EDIT_DELIVERY_ADDRESS);
    }, []);
    const editInvoiceAddress = useCallback((address) => {
        setCurrentAddress(address);
        setAddressComponentState(AddressComponentState.EDIT_INVOICE_ADDRESS);
    }, []);
    const showReadonlyAddresses = useCallback(() => {
        setAddressComponentState(AddressComponentState.READONLY_ADDRESS);
    }, []);
    const showSavedAddresses = useCallback(() => {
        setAddressComponentState(AddressComponentState.SHOW_SAVED_ADDRESSES);
    }, []);
    const scrollToTop = useCallback(() => {
        const element = document.getElementById(ADDRESS_SECTION_ID);
        if (element) {
            window.scroll({
                top: element.offsetTop + 100,
                left: 0,
                behavior: 'smooth',
            });
        }
    }, []);
    const discardChanges = useCallback(() => {
        scrollToTop();
        showReadonlyAddresses();
        setIsDeliveryAndInvoiceCollapsed?.(true);
    }, [scrollToTop, setIsDeliveryAndInvoiceCollapsed, showReadonlyAddresses]);
    useEffect(() => {
        const isDeliveryAndInvoicePresent = deliveryAddress && invoiceAddress;
        const isDeliveryValidatedWithUniserv = deliveryAddress?.addressValidatedWithUniserv;
        const isDeliveryAndInvoiceValid = deliveryAddress?.addressValidBasedOnRules && invoiceAddress?.addressValidBasedOnRules;
        if (isDeliveryAndInvoicePresent &&
            isDeliveryValidatedWithUniserv &&
            isDeliveryAndInvoiceValid &&
            !addressMessages &&
            isDeliveryAndInvoiceCollapsed) {
            showReadonlyAddresses();
        }
    }, [
        addressMessages,
        deliveryAddress,
        invoiceAddress,
        isDeliveryAndInvoiceCollapsed,
        showReadonlyAddresses,
    ]);
    const renderAddressComponent = (state) => {
        switch (state) {
            case AddressComponentState.READONLY_ADDRESS:
                return jsx(ReadonlyAddress, {});
            case AddressComponentState.ADD_NEW_DELIVERY_ADDRESS:
                return (jsx(DeliveryAddressForm, { isCombinedForm: isCombinedForm, showBackToListingLink: showBackToListingLink }));
            case AddressComponentState.EDIT_DELIVERY_ADDRESS:
                return (jsx(DeliveryAddressForm, { defaultValues: { delivery: currentAddress }, showBackToListingLink: showBackToListingLink }));
            case AddressComponentState.ADD_NEW_INVOICE_ADDRESS:
                return jsx(InvoiceAddressForm, {});
            case AddressComponentState.EDIT_INVOICE_ADDRESS:
                return jsx(InvoiceAddressForm, { defaultValues: { invoice: currentAddress } });
            case AddressComponentState.SHOW_SAVED_ADDRESSES: {
                return jsx(SavedAddresses, {});
            }
            default:
                return jsx(Loader, {});
        }
    };
    return (jsx(ErrorBoundary, { FallbackComponent: Fallback, onError: handleError, children: jsx(Suspense, { fallback: jsx(Loader, {}), children: jsxs(AddressActionsContext.Provider, { value: {
                    addNewDeliveryAddress,
                    editDeliveryAddress,
                    showSavedAddresses,
                    editInvoiceAddress,
                    discardChanges,
                    scrollToTop,
                }, children: [jsx("div", { id: ADDRESS_SECTION_ID }), jsx(FlagsProvider, { children: renderAddressComponent(addressComponentState) })] }) }) }));
};

export { AddressComponentSwitcher };
//# sourceMappingURL=address-component-switcher.js.map
